import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2, Gift } from "lucide-react";
import Message from "../components/Message";
import { GoogleColorIcon, MicrosoftColorIcon } from "./LoginV3Icons";

const PILL =
    "h-[46px] rounded-full px-4 text-[14.5px] font-medium transition-all duration-150 focus:outline-none";

function LastUsedBadge({ t }) {
    return (
        <span
            className="absolute -top-2 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-[#7c6bb0] text-white text-[10px] font-medium leading-none px-2 py-1 shadow-[0_2px_6px_-1px_rgb(124_107_176/0.45)] pointer-events-none select-none"
            aria-label={t("auth.loginV3.lastUsed")}
        >
            <span className="w-[5px] h-[5px] rounded-full bg-white/90" />
            {t("auth.loginV3.lastUsed")}
        </span>
    );
}

function SsoButton({ onClick, loading, disabled, icon, label, t, isLastUsed }) {
    return (
        <div className="relative">
            {isLastUsed && <LastUsedBadge t={t} />}
            <button
                type="button"
                onClick={onClick}
                disabled={disabled || loading}
                className={`${PILL} w-full inline-flex items-center justify-center gap-2.5 bg-white border ${isLastUsed ? "border-[#a594c8] ring-[3px] ring-[#a594c8]/15" : "border-[#e9e7ee]"} text-[#171a1f] hover:border-[#d8d4e0] hover:bg-[#fafafa] hover:shadow-sm focus-visible:border-[#7c6bb0] focus-visible:ring-[3px] focus-visible:ring-[#a594c8]/30 disabled:opacity-60 disabled:cursor-not-allowed`}
            >
                <span className="w-[18px] h-[18px] flex-shrink-0 inline-flex items-center justify-center">
                    {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#a594c8]" />
                    ) : (
                        icon
                    )}
                </span>
                <span>{loading ? t("auth.login.loading") : label}</span>
            </button>
        </div>
    );
}

function ReferralBanner({ t }) {
    return (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#e8def4] bg-gradient-to-r from-[#f3eff9] to-[#fde7f0] px-4 py-3">
            <span className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[#a594c8] to-[#e08eb4] text-white">
                <Gift className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <div className="text-left">
                <p className="text-[13.5px] font-medium text-[#5e4f8c] leading-tight">
                    {t("auth.signup.referralBonus")}
                </p>
                <p className="text-[12.5px] text-[#7c6bb0] leading-tight mt-0.5">
                    {t("auth.signup.referralBonusDescription")}
                </p>
            </div>
        </div>
    );
}

export default function SignupV3Card({
    isTokenStep,
    email,
    setEmail,
    error,
    successMessage,
    referralCode,
    onSignupSubmit,
    onTokenSubmit,
    onGoogle,
    onMicrosoft,
    onResend,
    isGoogleLoading,
    isMicrosoftLoading,
    isSignupLoading,
    isLoadingUser,
    isLoginLoading,
    isResending,
    loginError,
    requestCodeError,
    signupError,
    lastUsedMethod,
}) {
    const { t } = useTranslation();
    const [acceptedTerms, setAcceptedTerms] = React.useState(false);

    return (
        <form
            onSubmit={isTokenStep ? onTokenSubmit : onSignupSubmit}
            className="bg-white border border-[#e9e7ee] rounded-[20px] shadow-[0_1px_2px_rgb(23_26_31/0.04),0_24px_56px_-24px_rgb(124_107_176/0.18)] p-7 sm:p-8 animate-[cardIn_420ms_cubic-bezier(0.22,1,0.36,1)_both]"
        >
            {referralCode && !isTokenStep && <ReferralBanner t={t} />}

            {error && (
                <Message error className="mb-4">
                    {typeof error === "string"
                        ? error
                        : error?.message || t("auth.signup.somethingWentWrong")}
                </Message>
            )}
            {successMessage && (
                <Message success className="mb-4">
                    {successMessage}
                </Message>
            )}

            {!isTokenStep && (
                <>
                    <div className="grid gap-3 mb-5 pt-2">
                        <SsoButton
                            t={t}
                            onClick={onGoogle}
                            loading={isGoogleLoading}
                            icon={<GoogleColorIcon />}
                            label={t("auth.loginV3.continueWithGoogle")}
                            isLastUsed={lastUsedMethod === "google"}
                        />
                        <SsoButton
                            t={t}
                            onClick={onMicrosoft}
                            loading={isMicrosoftLoading}
                            icon={<MicrosoftColorIcon />}
                            label={t("auth.loginV3.continueWithMicrosoft")}
                            isLastUsed={lastUsedMethod === "microsoft"}
                        />
                    </div>

                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3.5 mt-1 mb-4 text-[12px] tracking-wide text-[#9ca3af]">
                        <span className="h-px bg-[#f1eff5]" />
                        <span>{t("auth.loginV3.orContinueWithEmail")}</span>
                        <span className="h-px bg-[#f1eff5]" />
                    </div>

                    {signupError && (
                        <Message error className="mb-3">
                            {signupError?.error ||
                                signupError?.message ||
                                t("auth.signup.somethingWentWrong")}
                        </Message>
                    )}

                    <div className="grid gap-1.5 mb-3.5">
                        <label htmlFor="fullName" className="text-[13.5px] font-medium text-[#171a1f]">
                            {t("auth.signup.fullName")}
                        </label>
                        <input
                            id="fullName"
                            name="fullName"
                            type="text"
                            autoComplete="name"
                            required
                            autoFocus
                            placeholder={t("auth.signup.fullNamePlaceholder")}
                            className="h-[46px] w-full appearance-none rounded-full border border-[#e9e7ee] bg-white px-4 text-[14.5px] text-[#171a1f] placeholder:text-[#9ca3af] outline-none transition-all hover:border-[#d8d4e0] focus:border-[#7c6bb0] focus:ring-[3px] focus:ring-[#a594c8]/30"
                        />
                    </div>

                    <div className="grid gap-1.5 mb-4">
                        <label htmlFor="email" className="text-[13.5px] font-medium text-[#171a1f]">
                            {t("auth.signup.emailLabel")}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("auth.loginV3.emailPlaceholder")}
                            className="h-[46px] w-full appearance-none rounded-full border border-[#e9e7ee] bg-white px-4 text-[14.5px] text-[#171a1f] placeholder:text-[#9ca3af] outline-none transition-all hover:border-[#d8d4e0] focus:border-[#7c6bb0] focus:ring-[3px] focus:ring-[#a594c8]/30"
                        />
                    </div>

                    <label htmlFor="terms" className="flex items-start gap-2.5 mb-5 cursor-pointer select-none">
                        <input
                            id="terms"
                            name="terms"
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            required
                            className="mt-[3px] h-[15px] w-[15px] rounded border-[#d8d4e0] text-[#7c6bb0] focus:ring-[#a594c8] focus:ring-offset-0"
                        />
                        <span className="text-[13px] text-[#6b7280] leading-snug">
                            {t("auth.signup.termsLabel")}{" "}
                            <a
                                href="https://www.agentui.ai/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-[#171a1f] border-b border-[#e9e7ee] hover:border-[#7c6bb0] transition-colors"
                            >
                                {t("auth.signup.termsLink")}
                            </a>
                        </span>
                    </label>

                    <button
                        type="submit"
                        disabled={isSignupLoading || isLoadingUser}
                        className={`${PILL} group w-full inline-flex items-center justify-center gap-2.5 bg-[#0a0d12] text-white border border-[#0a0d12] hover:bg-[#1f2937] hover:border-[#1f2937] disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                        {isSignupLoading || isLoadingUser
                            ? t("auth.login.sending")
                            : t("auth.signup.createAccount")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-[3px]" strokeWidth={1.8} />
                    </button>

                    <p className="mt-3.5 text-center text-[12.5px] leading-relaxed text-[#9ca3af]">
                        {t("auth.signupV3.helper")}{" "}
                        <a
                            href="https://help.agentui.ai/doc/how-email-code-login-works"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#6b7280] border-b border-[#e9e7ee] hover:text-[#171a1f] hover:border-[#6b7280] transition-colors"
                        >
                            {t("auth.signupV3.howItWorks")}
                        </a>
                    </p>
                </>
            )}

            {isTokenStep && (
                <>
                    <div className="mb-5 text-center">
                        <h2 className="text-[20px] font-medium text-[#171a1f] mb-1">
                            {t("auth.signupV3.tokenStepTitle")}
                        </h2>
                        <p className="text-[13.5px] text-[#6b7280]">
                            {t("auth.signupV3.tokenStepLede", { email })}
                        </p>
                    </div>

                    <input type="hidden" name="email" value={email} readOnly />

                    {requestCodeError && (
                        <Message error className="mb-3">
                            {requestCodeError?.message ??
                                requestCodeError?.error ??
                                t("auth.signup.failedToSendVerificationCode")}
                        </Message>
                    )}
                    {loginError && (
                        <Message error className="mb-3">
                            {loginError?.error ||
                                loginError?.message ||
                                t("auth.signup.verificationFailed")}
                        </Message>
                    )}

                    <div className="grid gap-1.5 mb-4">
                        <label htmlFor="token" className="text-[13.5px] font-medium text-[#171a1f]">
                            {t("auth.signup.verificationToken")}
                        </label>
                        <input
                            id="token"
                            name="token"
                            type="text"
                            autoComplete="off"
                            data-1p-ignore
                            data-lpignore="true"
                            data-form-type="other"
                            required
                            autoFocus
                            placeholder={t("auth.login.tokenPlaceholder")}
                            className="h-[46px] w-full appearance-none rounded-full border border-[#e9e7ee] bg-white px-4 text-[14.5px] text-[#171a1f] tracking-widest placeholder:text-[#9ca3af] placeholder:tracking-normal outline-none transition-all hover:border-[#d8d4e0] focus:border-[#7c6bb0] focus:ring-[3px] focus:ring-[#a594c8]/30"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoginLoading}
                        className={`${PILL} group w-full inline-flex items-center justify-center gap-2.5 bg-[#0a0d12] text-white border border-[#0a0d12] hover:bg-[#1f2937] disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                        {isLoginLoading
                            ? t("auth.login.loading")
                            : t("auth.signup.verifyAndSignIn")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-[3px]" strokeWidth={1.8} />
                    </button>

                    <div className="mt-4 text-center text-[13px] text-[#6b7280]">
                        <span className="block mb-1">{t("auth.signupV3.didntReceiveCode")}</span>
                        <button
                            type="button"
                            onClick={onResend}
                            disabled={isResending}
                            className="text-[#7c6bb0] hover:text-[#5e4f8c] font-medium disabled:opacity-50"
                        >
                            {isResending
                                ? t("auth.login.sending")
                                : t("auth.signupV3.requestAnotherCode")}
                        </button>
                    </div>
                </>
            )}
        </form>
    );
}
